import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  RadioGroup,
  Radio,
  Divider,
  CircularProgress,
  Box,
  Theme,
} from '@material-ui/core';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import GetAppIcon from '@material-ui/icons/GetApp';
import ArchiveIcon from '@material-ui/icons/Archive';
import { makeStyles } from '@material-ui/styles';
import Modal from '@material-ui/core/Modal';
import Backdrop from '@material-ui/core/Backdrop';
import Fade from '@material-ui/core/Fade';
import { useSelector, shallowEqual } from 'react-redux';
import { toast } from 'react-toastify';

import Page from '../../components/page';
import { client, APIURL } from '../../services/at-app-api';
import { UserRoles } from '../../settings/user-roles';

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const useStyles = makeStyles((theme: Theme) => ({
  root: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
  },
  warningHeader: {
    background: '#b71c1c',
    color: '#fff',
    padding: theme.spacing(2, 3),
    borderRadius: 4,
    marginBottom: theme.spacing(3),
  },
  sectionCard: {
    marginBottom: theme.spacing(2),
    width: '100%',
    maxHeight: 'calc(100vh - 300px)',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  sectionGrid: {
    height: 'calc(100vh - 260px)',
    minHeight: 0,
    overflow: 'hidden',
  },
  sectionGridItem: {
    display: 'flex',
    minHeight: 0,
  },
  sectionContent: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
    flexDirection: 'column',
  },
  sectionTitle: {
    fontWeight: 700,
    color: theme.palette.text.secondary,
    fontSize: 13,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: theme.spacing(1),
  },
  checkList: {
    flex: 1,
    minHeight: 0,
    maxHeight: 'none',
    overflowY: 'auto',
    paddingLeft: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'nowrap',
    alignItems: 'flex-start',
    '& .MuiFormControlLabel-root': {
      width: '100%',
      marginRight: 0,
    },
  },
  deleteButton: {
    background: '#b71c1c',
    color: '#fff',
    marginTop: theme.spacing(2),
    textTransform: 'none',
    '&:hover': {
      background: '#7f0000',
    },
    '&:disabled': {
      background: theme.palette.grey[400],
    },
  },
  // Confirmation modal
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalPaper: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 4,
    boxShadow: theme.shadows[5],
    maxWidth: 500,
    width: '90%',
  },
  modalHeader: {
    backgroundColor: '#b71c1c',
    color: '#fff',
    padding: theme.spacing(2),
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
  },
  modalContent: {
    padding: theme.spacing(3),
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  confirmButton: {
    background: '#b71c1c',
    color: '#fff',
    '&:hover': { background: '#7f0000' },
  },
  // Attestation
  attestationCard: {
    marginTop: theme.spacing(3),
    border: '2px solid #388e3c',
  },
  attestationHeader: {
    background: '#388e3c',
    color: '#fff',
    padding: theme.spacing(2),
    borderTopLeftRadius: 2,
    borderTopRightRadius: 2,
  },
  attestationBody: {
    padding: theme.spacing(3),
    fontFamily: 'monospace',
    fontSize: 13,
    whiteSpace: 'pre-wrap',
    background: theme.palette.grey[50],
  },
  downloadButton: {
    marginTop: theme.spacing(2),
  },
}));

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface SelectionItem {
  uid: string;
  name: string;
  active: boolean;
}

interface Patient {
  uid: string;
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  active: boolean;
  facility?: string;
  supervisorUid?: string;
}

interface PurgedPatient {
  uid: string;
  firstName: string;
  lastName: string;
  username: string;
  email: string;
}

interface PurgeResult {
  purgedAtUtc: string;
  performedBy: string;
  performedByEmail: string;
  selectionSummary: string;
  deletedPatients: PurgedPatient[];
  deletedTherapistCount: number;
  deletedClinicCount: number;
  hospitalDeleted: boolean;
  verificationCount: number;
  verificationPassed: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function buildAttestationText(result: PurgeResult): string {
  const lines: string[] = [];
  lines.push('='.repeat(72));
  lines.push('      CERTIFICATION OF DATA DESTRUCTION');
  lines.push('      AugmentTherapy Platform');
  lines.push('='.repeat(72));
  lines.push('');
  lines.push(`Date/Time (UTC):  ${new Date(result.purgedAtUtc).toUTCString()}`);
  lines.push(`Performed By:     ${result.performedBy} <${result.performedByEmail}>`);
  lines.push(`Scope:            ${result.selectionSummary || '(not specified)'}`);
  lines.push('');

  if (result.hospitalDeleted || result.deletedClinicCount > 0 || result.deletedTherapistCount > 0) {
    lines.push('-'.repeat(72));
    lines.push('ORGANIZATIONAL RECORDS DELETED');
    lines.push('-'.repeat(72));
    lines.push('');
    if (result.hospitalDeleted)
      lines.push('  Hospital facility record permanently deleted.');
    if (result.deletedClinicCount > 0)
      lines.push(`  ${result.deletedClinicCount} clinic facility record(s) permanently deleted.`);
    if (result.deletedTherapistCount > 0)
      lines.push(`  ${result.deletedTherapistCount} therapist account(s) permanently deleted.`);
    lines.push('');
  }

  lines.push('-'.repeat(72));
  lines.push('PERMANENTLY DELETED PATIENT RECORDS');
  lines.push('-'.repeat(72));
  lines.push('');

  if (result.deletedPatients.length === 0) {
    lines.push('  (none)');
  } else {
    result.deletedPatients.forEach((p, idx) => {
      lines.push(`  ${idx + 1}. ${p.firstName} ${p.lastName}`);
      lines.push(`     UID:      ${p.uid}`);
      lines.push(`     Username: ${p.username}`);
      lines.push(`     Email:    ${p.email}`);
      lines.push('');
    });
  }

  lines.push('-'.repeat(72));
  lines.push('VERIFICATION');
  lines.push('-'.repeat(72));
  lines.push('');
  if (result.verificationPassed) {
    lines.push('  PASSED: A post-deletion query for all above patient UIDs in the');
    lines.push('  [user] table returned 0 records, confirming complete removal.');
  } else {
    lines.push(`  WARNING: Post-deletion query returned ${result.verificationCount} record(s).`);
    lines.push('  Manual review is required.');
  }
  lines.push('');
  lines.push('='.repeat(72));
  lines.push('STATEMENT');
  lines.push('='.repeat(72));
  lines.push('');
  lines.push('This document certifies that all Protected Health Information (PHI)');
  lines.push('for the patients listed above has been permanently and irreversibly');
  lines.push('deleted from the AugmentTherapy system, including all session data,');
  lines.push('session task recordings, protocol assignments, therapist notes, and');
  lines.push('user account information. This deletion is not recoverable.');
  lines.push('');
  lines.push('='.repeat(72));
  return lines.join('\n');
}

function downloadTextFile(filename: string, text: string) {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const DataDestructionPage: React.FC = () => {
  const classes = useStyles();
  const api = client();

  // Redux: current user role/name
  const profile = useSelector((state: any) => state.user?.profile, shallowEqual);

  // ----- Step state -----
  const [hospitals, setHospitals] = useState<SelectionItem[]>([]);
  const [selectedHospital, setSelectedHospital] = useState<SelectionItem | null>(null);

  const [clinics, setClinics] = useState<SelectionItem[]>([]);
  const [selectedClinicUids, setSelectedClinicUids] = useState<Set<string>>(new Set());

  const [therapists, setTherapists] = useState<SelectionItem[]>([]);
  const [selectedTherapistUids, setSelectedTherapistUids] = useState<Set<string>>(new Set());

  const [patients, setPatients] = useState<Patient[]>([]);
  const [selectedPatientUids, setSelectedPatientUids] = useState<Set<string>>(new Set());

  // ----- Loading -----
  const [loadingHospitals, setLoadingHospitals] = useState(false);
  const [loadingClinics, setLoadingClinics] = useState(false);
  const [loadingTherapists, setLoadingTherapists] = useState(false);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [purging, setPurging] = useState(false);

  // ----- Modal -----
  const [confirmOpen, setConfirmOpen] = useState(false);

  // ----- Result -----
  const [purgeResult, setPurgeResult] = useState<PurgeResult | null>(null);

  // ---------------------------------------------------------------------------
  // Load hospitals on mount
  // ---------------------------------------------------------------------------
  useEffect(() => {
    setLoadingHospitals(true);
    api.get(`${APIURL}/api/facility`, { params: { type: 1, includeArchived: true } })
      .then((res: any) => {
        const items: SelectionItem[] = (res.data?.data ?? []).map((f: any) => ({
          uid: f.uid,
          name: f.name,
          active: f.status !== 0,
        }));
        setHospitals(items);
      })
      .catch(() => toast.error('Failed to load hospitals.'))
      .finally(() => setLoadingHospitals(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------------------------------------------------------
  // Load clinics when hospital changes
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!selectedHospital) {
      setClinics([]);
      setSelectedClinicUids(new Set());
      setTherapists([]);
      setSelectedTherapistUids(new Set());
      setPatients([]);
      setSelectedPatientUids(new Set());
      return;
    }
    setLoadingClinics(true);
    api.get(`${APIURL}/api/facility`, {
      params: { type: 2, parentUid: selectedHospital.uid, includeArchived: true },
    })
      .then((res: any) => {
        const items: SelectionItem[] = (res.data?.data ?? []).map((f: any) => ({
          uid: f.uid,
          name: f.name,
          active: f.status !== 0,
        }));
        setClinics(items);
        setSelectedClinicUids(new Set());
        setTherapists([]);
        setSelectedTherapistUids(new Set());
        setPatients([]);
        setSelectedPatientUids(new Set());
      })
      .catch(() => toast.error('Failed to load clinics.'))
      .finally(() => setLoadingClinics(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHospital]);

  // ---------------------------------------------------------------------------
  // Load therapists when selected clinics change
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (selectedClinicUids.size === 0) {
      setTherapists([]);
      setSelectedTherapistUids(new Set());
      setPatients([]);
      setSelectedPatientUids(new Set());
      return;
    }
    setLoadingTherapists(true);
    api.post(`${APIURL}/api/user/batch-by-facilities`, Array.from(selectedClinicUids))
      .then((res: any) => {
        const merged: SelectionItem[] = (res.data?.data ?? []).map((u: any) => ({
          uid: u.uid,
          name: `${u.firstName} ${u.lastName}`,
          active: u.active !== false,
        }));
        setTherapists(merged);
        setSelectedTherapistUids(new Set());
        setPatients([]);
        setSelectedPatientUids(new Set());
      })
      .catch(() => toast.error('Failed to load therapists.'))
      .finally(() => setLoadingTherapists(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClinicUids]);

  // ---------------------------------------------------------------------------
  // Load patients when selected therapists change
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (selectedTherapistUids.size === 0) {
      setPatients([]);
      setSelectedPatientUids(new Set());
      return;
    }
    setLoadingPatients(true);
    api.post(`${APIURL}/api/user/enduser-batch`, Array.from(selectedTherapistUids))
      .then((res: any) => {
        const merged: Patient[] = (res.data?.data ?? []).map((p: any) => ({
          uid: p.uid,
          firstName: p.firstName,
          lastName: p.lastName,
          username: p.username,
          email: p.email,
          active: p.active !== false,
          supervisorUid: p.supervisorUid,
        }));
        setPatients(merged);
        setSelectedPatientUids(new Set());
      })
      .catch(() => toast.error('Failed to load patients.'))
      .finally(() => setLoadingPatients(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTherapistUids]);

  // ---------------------------------------------------------------------------
  // Toggle helpers
  // ---------------------------------------------------------------------------
  const toggleClinic = useCallback((uid: string) => {
    setSelectedClinicUids((prev) => {
      const next = new Set(prev);
      next.has(uid) ? next.delete(uid) : next.add(uid);
      return next;
    });
  }, []);

  const toggleTherapist = useCallback((uid: string) => {
    setSelectedTherapistUids((prev) => {
      const next = new Set(prev);
      next.has(uid) ? next.delete(uid) : next.add(uid);
      return next;
    });
  }, []);

  const togglePatient = useCallback((uid: string) => {
    setSelectedPatientUids((prev) => {
      const next = new Set(prev);
      next.has(uid) ? next.delete(uid) : next.add(uid);
      return next;
    });
  }, []);

  const toggleAllClinics = useCallback(() => {
    setSelectedClinicUids((prev) => {
      const allSelected = clinics.length > 0 && clinics.every((c) => prev.has(c.uid));
      return allSelected ? new Set() : new Set(clinics.map((c) => c.uid));
    });
  }, [clinics]);

  const toggleAllTherapists = useCallback(() => {
    setSelectedTherapistUids((prev) => {
      const allSelected = therapists.length > 0 && therapists.every((t) => prev.has(t.uid));
      return allSelected ? new Set() : new Set(therapists.map((t) => t.uid));
    });
  }, [therapists]);

  const toggleAllPatients = useCallback(() => {
    if (selectedPatientUids.size === patients.length) {
      setSelectedPatientUids(new Set());
    } else {
      setSelectedPatientUids(new Set(patients.map((p) => p.uid)));
    }
  }, [patients, selectedPatientUids]);

  // ---------------------------------------------------------------------------
  // Effective scope: deepest selected level takes precedence
  // ---------------------------------------------------------------------------
  const effectiveScope: 'patients' | 'therapists' | 'clinics' | 'hospital' | null =
    selectedPatientUids.size > 0 ? 'patients'
    : selectedTherapistUids.size > 0 ? 'therapists'
    : selectedClinicUids.size > 0 ? 'clinics'
    : selectedHospital ? 'hospital'
    : null;

  // ---------------------------------------------------------------------------
  // Build selection summary string for attestation
  // ---------------------------------------------------------------------------
  const buildSelectionSummary = (): string => {
    const parts: string[] = [];
    if (selectedHospital) parts.push(`Hospital: ${selectedHospital.name}`);
    const clinicNames = clinics
      .filter((c) => selectedClinicUids.has(c.uid))
      .map((c) => c.name);
    if (clinicNames.length) parts.push(`Clinics: ${clinicNames.join(', ')}`);
    const therapistNames = therapists
      .filter((t) => selectedTherapistUids.has(t.uid))
      .map((t) => t.name);
    if (therapistNames.length) parts.push(`Therapists: ${therapistNames.join(', ')}`);
    if (effectiveScope === 'therapists') parts.push('Scope: therapist-level (all patients included)');
    else if (effectiveScope === 'clinics') parts.push('Scope: clinic-level (all therapists + patients included)');
    else if (effectiveScope === 'hospital') parts.push('Scope: hospital-level (all sub-items included)');
    return parts.join(' | ');
  };

  // ---------------------------------------------------------------------------
  // Build delete button label
  // ---------------------------------------------------------------------------
  const deleteButtonLabel = (): string => {
    if (purging) return 'Deleting…';
    switch (effectiveScope) {
      case 'patients':
        return `Permanently delete ${selectedPatientUids.size} selected patient${selectedPatientUids.size !== 1 ? 's' : ''}`;
      case 'therapists':
        return `Permanently delete ${selectedTherapistUids.size} selected therapist${selectedTherapistUids.size !== 1 ? 's' : ''} and all their patients`;
      case 'clinics':
        return `Permanently delete ${selectedClinicUids.size} selected clinic${selectedClinicUids.size !== 1 ? 's' : ''}, their therapists, and all patients`;
      case 'hospital':
        return `Permanently delete hospital “${selectedHospital!.name}” and all sub-items`;
      default:
        return 'Permanently delete selected records';
    }
  };

  // ---------------------------------------------------------------------------
  // Build modal scope description
  // ---------------------------------------------------------------------------
  const modalScopeDescription = (): React.ReactNode => {
    switch (effectiveScope) {
      case 'patients':
        return (
          <>permanently delete all PHI for{' '}
            <strong>{selectedPatientUids.size}</strong> patient{selectedPatientUids.size !== 1 ? 's' : ''}</>
        );
      case 'therapists': {
        const names = therapists
          .filter((t) => selectedTherapistUids.has(t.uid))
          .map((t) => t.name)
          .join(', ');
        return (
          <>permanently delete <strong>{selectedTherapistUids.size}</strong> therapist account{selectedTherapistUids.size !== 1 ? 's' : ''} ({names}) and <strong>all their patients</strong></>
        );
      }
      case 'clinics': {
        const names = clinics
          .filter((c) => selectedClinicUids.has(c.uid))
          .map((c) => c.name)
          .join(', ');
        return (
          <>permanently delete <strong>{selectedClinicUids.size}</strong> clinic{selectedClinicUids.size !== 1 ? 's' : ''} ({names}), <strong>all their therapists</strong>, and <strong>all patients</strong></>
        );
      }
      case 'hospital':
        return (
          <>permanently delete hospital <strong>"{selectedHospital!.name}"</strong> and <strong>all its clinics, therapists, and patients</strong></>
        );
      default:
        return <>permanently delete the selected records</>;
    }
  };

  // ---------------------------------------------------------------------------
  // Execute purge
  // ---------------------------------------------------------------------------
  const executePurge = async () => {
    setConfirmOpen(false);
    setPurging(true);

    // Build request body matching the effective scope
    const scopePayload =
      effectiveScope === 'patients'   ? { patientUids:   Array.from(selectedPatientUids) }
      : effectiveScope === 'therapists' ? { therapistUids: Array.from(selectedTherapistUids) }
      : effectiveScope === 'clinics'    ? { clinicUids:    Array.from(selectedClinicUids) }
      : effectiveScope === 'hospital'   ? { hospitalUid:   selectedHospital!.uid }
      : {};

    try {
      const response = await api.post(`${APIURL}/api/datadestruction/purge`, {
        ...scopePayload,
        selectionSummary: buildSelectionSummary(),
      });
      setPurgeResult(response.data);

      const deletedPatientUids = new Set<string>(
        (response.data.deletedPatients ?? []).map((p: PurgedPatient) => p.uid)
      );

      // Clean up UI state at the appropriate level
      if (effectiveScope === 'hospital') {
        setSelectedHospital(null); // cascades via useEffect
      } else if (effectiveScope === 'clinics') {
        setClinics((prev) => prev.filter((c) => !selectedClinicUids.has(c.uid)));
        setSelectedClinicUids(new Set());
        setTherapists([]);
        setPatients([]);
      } else if (effectiveScope === 'therapists') {
        setTherapists((prev) => prev.filter((t) => !selectedTherapistUids.has(t.uid)));
        setSelectedTherapistUids(new Set());
        setPatients([]);
      } else {
        setPatients((prev) => prev.filter((p) => !deletedPatientUids.has(p.uid)));
        setSelectedPatientUids(new Set());
      }

      const patientCount = response.data.deletedPatients?.length ?? 0;
      const therapistCount = response.data.deletedTherapistCount ?? 0;
      const clinicCount = response.data.deletedClinicCount ?? 0;
      const parts: string[] = [];
      if (patientCount > 0) parts.push(`${patientCount} patient${patientCount !== 1 ? 's' : ''}`);
      if (therapistCount > 0) parts.push(`${therapistCount} therapist${therapistCount !== 1 ? 's' : ''}`);
      if (clinicCount > 0) parts.push(`${clinicCount} clinic${clinicCount !== 1 ? 's' : ''}`);
      if (response.data.hospitalDeleted) parts.push('1 hospital');
      toast.success(`Permanently deleted: ${parts.join(', ') || '0 records'}.`);
    } catch (err) {
      const error = err as any;
      const msg = error?.response?.data?.errorDescription ?? 'Purge operation failed. Please try again.';
      toast.error(msg);
    } finally {
      setPurging(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Download attestation
  // ---------------------------------------------------------------------------
  const downloadAttestation = () => {
    if (!purgeResult) return;
    const text = buildAttestationText(purgeResult);
    const ts = new Date(purgeResult.purgedAtUtc)
      .toISOString()
      .replace(/[:.]/g, '-')
      .slice(0, 19);
    downloadTextFile(`DataDestruction_Attestation_${ts}.txt`, text);
  };

  // Only Superuser can see this page
  if (profile?.role !== UserRoles.SUPERUSER) {
    return (
      <Page title="Access Denied">
        <Container maxWidth="md" className={classes.root}>
          <Typography variant="h5" color="error">
            Access Denied — this page is restricted to superusers.
          </Typography>
        </Container>
      </Page>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <Page title="Data Destruction">
      <Container maxWidth="lg" className={classes.root}>
        {/* Warning banner */}
        <div className={classes.warningHeader}>
          <Typography variant="h5" style={{ fontWeight: 700, color: '#fff' }}>
            &#9888; Data Destruction Utility
          </Typography>
          <Typography variant="body2" style={{ marginTop: 4, color: '#fff' }}>
            This utility permanently and irreversibly deletes all Protected Health
            Information (PHI) for the selected patients. This action CANNOT be
            undone. A downloadable attestation will be provided as proof of deletion.
          </Typography>
        </div>

        <Grid container spacing={3} className={classes.sectionGrid}>
          {/* STEP 1 – Hospital */}
          <Grid item xs={12} md={3} className={classes.sectionGridItem}>
            <Card className={classes.sectionCard}>
              <CardContent className={classes.sectionContent}>
                <Typography className={classes.sectionTitle}>Step 1 — Hospital ({hospitals.length})</Typography>
                {loadingHospitals ? (
                  <CircularProgress size={20} />
                ) : (
                  <RadioGroup
                    value={selectedHospital?.uid ?? ''}
                    onChange={(e) => {
                      const uid = e.target.value;
                      const hospital = hospitals.find((h) => h.uid === uid);
                      setSelectedHospital(hospital || null);
                    }}
                    className={classes.checkList}
                  >
                    {hospitals.map((h) => (
                      <FormControlLabel
                        key={h.uid}
                        value={h.uid}
                        control={<Radio color="primary" />}
                        label={
                          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            {!h.active && <ArchiveIcon style={{ fontSize: 14, color: '#888' }} />}
                            {h.name}
                          </span>
                        }
                      />
                    ))}
                    {hospitals.length === 0 && (
                      <Typography variant="caption" color="textSecondary">
                        No hospitals found.
                      </Typography>
                    )}
                  </RadioGroup>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* STEP 2 – Clinics */}
          <Grid item xs={12} md={3} className={classes.sectionGridItem}>
            <Card className={classes.sectionCard}>
              <CardContent className={classes.sectionContent}>
                <Typography className={classes.sectionTitle}>Step 2 — Clinics ({clinics.length})</Typography>
                {loadingClinics ? (
                  <CircularProgress size={20} />
                ) : (
                  <>
                    {clinics.length > 0 && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={clinics.every((c) => selectedClinicUids.has(c.uid))}
                            indeterminate={
                              clinics.some((c) => selectedClinicUids.has(c.uid)) &&
                              !clinics.every((c) => selectedClinicUids.has(c.uid))
                            }
                            onChange={toggleAllClinics}
                            color="primary"
                          />
                        }
                        label={<strong>Select All</strong>}
                      />
                    )}
                    <FormGroup className={classes.checkList}>
                      {clinics.map((c) => (
                        <FormControlLabel
                          key={c.uid}
                          control={
                            <Checkbox
                              checked={selectedClinicUids.has(c.uid)}
                              onChange={() => toggleClinic(c.uid)}
                              color="primary"
                            />
                          }
                          label={
                            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              {!c.active && <ArchiveIcon style={{ fontSize: 14, color: '#888' }} />}
                              {c.name}
                            </span>
                          }
                        />
                      ))}
                      {!selectedHospital && (
                        <Typography variant="caption" color="textSecondary">
                          Select a hospital first.
                        </Typography>
                      )}
                      {selectedHospital && clinics.length === 0 && !loadingClinics && (
                        <Typography variant="caption" color="textSecondary">
                          No clinics found.
                        </Typography>
                      )}
                    </FormGroup>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* STEP 3 – Therapists */}
          <Grid item xs={12} md={3} className={classes.sectionGridItem}>
            <Card className={classes.sectionCard}>
              <CardContent className={classes.sectionContent}>
                <Typography className={classes.sectionTitle}>Step 3 — Therapists ({therapists.length})</Typography>
                {loadingTherapists ? (
                  <CircularProgress size={20} />
                ) : (
                  <>
                    {therapists.length > 0 && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={therapists.every((t) => selectedTherapistUids.has(t.uid))}
                            indeterminate={
                              therapists.some((t) => selectedTherapistUids.has(t.uid)) &&
                              !therapists.every((t) => selectedTherapistUids.has(t.uid))
                            }
                            onChange={toggleAllTherapists}
                            color="primary"
                          />
                        }
                        label={<strong>Select All</strong>}
                      />
                    )}
                    <FormGroup className={classes.checkList}>
                      {therapists.map((t) => (
                        <FormControlLabel
                          key={t.uid}
                          control={
                            <Checkbox
                              checked={selectedTherapistUids.has(t.uid)}
                              onChange={() => toggleTherapist(t.uid)}
                              color="primary"
                            />
                          }
                          label={
                            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              {!t.active && <ArchiveIcon style={{ fontSize: 14, color: '#888' }} />}
                              {t.name}
                            </span>
                          }
                        />
                      ))}
                      {selectedClinicUids.size === 0 && (
                        <Typography variant="caption" color="textSecondary">
                          Select one or more clinics first.
                        </Typography>
                      )}
                      {selectedClinicUids.size > 0 && therapists.length === 0 && !loadingTherapists && (
                        <Typography variant="caption" color="textSecondary">
                          No therapists found.
                        </Typography>
                      )}
                    </FormGroup>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* STEP 4 – Patients */}
          <Grid item xs={12} md={3} className={classes.sectionGridItem}>
            <Card className={classes.sectionCard}>
              <CardContent className={classes.sectionContent}>
                <Typography className={classes.sectionTitle}>Step 4 — Patients ({patients.length})</Typography>
                {loadingPatients ? (
                  <CircularProgress size={20} />
                ) : (
                  <>
                    {patients.length > 0 && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedPatientUids.size === patients.length && patients.length > 0}
                            indeterminate={
                              selectedPatientUids.size > 0 &&
                              selectedPatientUids.size < patients.length
                            }
                            onChange={toggleAllPatients}
                            color="secondary"
                          />
                        }
                        label={<strong>Select All</strong>}
                      />
                    )}
                    {patients.length > 0 ? (
                      <FormGroup className={classes.checkList}>
                        {patients.map((p) => (
                          <FormControlLabel
                            key={p.uid}
                            control={
                              <Checkbox
                                checked={selectedPatientUids.has(p.uid)}
                                onChange={() => togglePatient(p.uid)}
                                color="secondary"
                              />
                            }
                            label={
                              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                {!p.active && <ArchiveIcon style={{ fontSize: 14, color: '#888' }} />}
                                {p.firstName} {p.lastName}
                              </span>
                            }
                          />
                        ))}
                      </FormGroup>
                    ) : (
                      <Typography variant="caption" color="textSecondary">
                        {selectedTherapistUids.size === 0
                          ? 'Select one or more therapists first.'
                          : 'No patients found.'}
                      </Typography>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Delete button */}
        <Box display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            className={classes.deleteButton}
            startIcon={purging ? <CircularProgress size={16} color="inherit" /> : <DeleteForeverIcon />}
            disabled={effectiveScope === null || purging}
            onClick={() => setConfirmOpen(true)}
          >
            {deleteButtonLabel()}
          </Button>
        </Box>

        {/* Attestation result */}
        {purgeResult && (
          <Card className={classes.attestationCard}>
            <div className={classes.attestationHeader}>
              <Typography variant="h6" style={{ fontWeight: 700, color: '#fff' }}>
                ✓ Deletion Complete — Attestation
              </Typography>
            </div>
            <CardContent>
              <Typography variant="body2" style={{ marginBottom: 8 }}>
                Permanently deleted on{' '}
                <strong>{new Date(purgeResult.purgedAtUtc).toUTCString()}</strong> by{' '}
                <strong>{purgeResult.performedBy}</strong>:{' '}
                <strong>{purgeResult.deletedPatients.length}</strong> patient{purgeResult.deletedPatients.length !== 1 ? 's' : ''}
                {purgeResult.deletedTherapistCount > 0 && <>, <strong>{purgeResult.deletedTherapistCount}</strong> therapist{purgeResult.deletedTherapistCount !== 1 ? 's' : ''}</>}
                {purgeResult.deletedClinicCount > 0 && <>, <strong>{purgeResult.deletedClinicCount}</strong> clinic{purgeResult.deletedClinicCount !== 1 ? 's' : ''}</>}
                {purgeResult.hospitalDeleted && <>, <strong>1 hospital</strong></>}.
                {' '}{purgeResult.verificationPassed ? (
                  <span style={{ color: '#388e3c' }}>Verification PASSED — 0 patient records remain.</span>
                ) : (
                  <span style={{ color: '#b71c1c' }}>Verification WARNING — {purgeResult.verificationCount} record(s) still found. Manual review required.</span>
                )}
              </Typography>
              <pre className={classes.attestationBody}>
                {buildAttestationText(purgeResult)}
              </pre>
              <Button
                variant="contained"
                color="primary"
                startIcon={<GetAppIcon />}
                className={classes.downloadButton}
                onClick={downloadAttestation}
              >
                Download Attestation
              </Button>
            </CardContent>
          </Card>
        )}
      </Container>

      {/* Confirmation modal */}
      <Modal
        className={classes.modal}
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
      >
        <Fade in={confirmOpen}>
          <div className={classes.modalPaper}>
            <div className={classes.modalHeader}>
              <Typography variant="h6" style={{ fontWeight: 700, color: '#fff' }}>
                &#9888; Confirm Permanent Deletion
              </Typography>
            </div>
            <div className={classes.modalContent}>
              <Typography variant="body1" style={{ marginBottom: 12, fontSize: 16 }}>
                Are you sure you want to {modalScopeDescription()}? This will
                irreversibly remove all session data, protocol assignments, therapist
                notes, and account information. This action <strong>CANNOT be undone</strong>.
              </Typography>
            </div>
            <div className={classes.modalActions}>
              <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                className={classes.confirmButton}
                onClick={executePurge}
              >
                OK — Delete Permanently
              </Button>
            </div>
          </div>
        </Fade>
      </Modal>
    </Page>
  );
};

export default DataDestructionPage;
